import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  AdminRecipe,
  AdminRecipePayload,
  CountryOption,
  RecipeAccessType,
  RecipeCategory,
  RecipeCost,
  RecipeDifficulty,
  RecipeIngredient,
  RecipeStep,
} from '../../core/models/admin-recipe.models';
import { API_BASE_URL } from '../../core/config/api.config';
import { AdminRecipesService } from '../../core/services/admin-recipes.service';
import { AuthService } from '../../core/services/auth.service';
import { StorageService } from '../../core/services/storage.service';

@Component({
  selector: 'app-recipes-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './recipes-page.component.html',
  styleUrl: './recipes-page.component.scss',
})
export class RecipesPageComponent implements OnInit {
  private readonly recipesService = inject(AdminRecipesService);
  private readonly storageService = inject(StorageService);
  private readonly auth = inject(AuthService);
  private readonly fb = inject(FormBuilder);
  private readonly statusesToLoad = ['APPROVED', 'REVIEW', 'DENIED', 'DRAFT', 'UPDATED'];

  readonly costs: RecipeCost[] = ['CHEAP', 'MEDIUM', 'EXPENSIVE'];
  readonly difficulties: RecipeDifficulty[] = ['EASY', 'MEDIUM', 'HARD'];
  readonly accessTypes: RecipeAccessType[] = ['FREE', 'SUBSCRIBERS_ONLY', 'PAID'];

  recipes: AdminRecipe[] = [];
  allRecipes: AdminRecipe[] = [];
  filteredRecipes: AdminRecipe[] = [];
  categories: RecipeCategory[] = [];
  countries: CountryOption[] = [];

  loading = false;
  saving = false;
  uploadingCover = false;
  importRunning = false;
  deletingRecipeId: number | null = null;
  editingRecipeId: number | null = null;
  showEditor = false;

  searchQuery = '';
  currentPage = 0;
  pageSize = 15;
  totalPages = 1;
  totalItems = 0;

  errorMessage = '';
  successMessage = '';
  importSummary = '';
  importFailures: string[] = [];
  importFile: File | null = null;

  ingredientDialogOpen = false;
  ingredientDialogIndex: number | null = null;
  stepDialogOpen = false;
  stepDialogIndex: number | null = null;

  readonly form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(4)]],
    description: ['', [Validators.required, Validators.minLength(10)]],
    cost: ['MEDIUM', Validators.required],
    coverImage: ['', Validators.required],
    categoryId: [null as number | null, Validators.required],
    difficulty: ['MEDIUM', Validators.required],
    cookingTime: [30, [Validators.required, Validators.min(1)]],
    numberOfPersons: [2, [Validators.required, Validators.min(1)]],
    countries: this.fb.control<string[]>([], Validators.required),
    accessType: ['FREE', Validators.required],
    discountEnabled: [false],
    ingredients: this.fb.array([]),
    steps: this.fb.array([]),
  });

  readonly ingredientDialogForm = this.fb.group({
    title: [''],
    content: [''],
    mandatory: [true],
    image: [''],
    price: [null as number | null],
    priceFormatted: [''],
  });

  readonly stepDialogForm = this.fb.group({
    title: [''],
    content: [''],
    mandatory: [true],
    image: [''],
  });

  get ingredientsArray(): FormArray {
    return this.form.get('ingredients') as FormArray;
  }

  get stepsArray(): FormArray {
    return this.form.get('steps') as FormArray;
  }

  ngOnInit(): void {
    this.resetForm();
    this.loadCategories();
    this.loadCountries();
    this.loadRecipes();
  }

  loadRecipes(): void {
    this.loading = true;
    this.errorMessage = '';
    this.currentPage = 0;

    this.fetchAllRecipes()
      .then((recipes) => {
        this.allRecipes = recipes;
        this.applySearch(this.searchQuery);
      })
      .catch((error) => {
        this.errorMessage = this.extractErrorMessage(error, 'Impossible de charger les recettes.');
      })
      .finally(() => {
        this.loading = false;
      });
  }

  loadCategories(): void {
    this.recipesService.getCategories(0, 200).subscribe({
      next: (response) => {
        this.categories = response.items ?? [];
      },
      error: (error) => {
        this.errorMessage = error?.error?.message ?? 'Impossible de charger les catégories.';
      },
    });
  }

  loadCountries(): void {
    this.recipesService.getCountries().subscribe({
      next: (countries) => {
        this.countries = [...(countries ?? [])].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      },
      error: () => {
        this.countries = [];
      },
    });
  }

  applySearch(query: string): void {
    this.searchQuery = query;
    const normalized = query.trim().toLowerCase();
    this.filteredRecipes = this.allRecipes.filter((recipe) => {
      if (!normalized) {
        return true;
      }
      const categoryName = this.categoryLabel(recipe.categoryId);
      const haystack = `${recipe.name} ${recipe.description} ${categoryName}`.toLowerCase();
      return haystack.includes(normalized);
    });
    this.totalItems = this.filteredRecipes.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    this.currentPage = Math.min(this.currentPage, Math.max(this.totalPages - 1, 0));
    this.updateVisibleRecipes();
  }

  previousPage(): void {
    if (this.currentPage <= 0) {
      return;
    }
    this.currentPage -= 1;
    this.updateVisibleRecipes();
  }

  nextPage(): void {
    if (this.currentPage + 1 >= this.totalPages) {
      return;
    }
    this.currentPage += 1;
    this.updateVisibleRecipes();
  }

  startCreate(): void {
    this.showEditor = true;
    this.resetForm();
    this.successMessage = '';
  }

  closeEditor(): void {
    this.showEditor = false;
    this.editingRecipeId = null;
    this.ingredientDialogOpen = false;
    this.stepDialogOpen = false;
  }

  startEdit(recipe: AdminRecipe): void {
    if (!this.canEdit(recipe)) {
      this.errorMessage = 'Modification non autorisée pour cette recette.';
      return;
    }

    this.showEditor = true;
    this.editingRecipeId = recipe.id;
    this.errorMessage = '';
    this.successMessage = '';

    this.form.patchValue({
      name: recipe.name,
      description: recipe.description,
      cost: recipe.cost,
      coverImage: recipe.coverImage,
      categoryId: recipe.categoryId,
      difficulty: recipe.difficulty,
      cookingTime: recipe.cookingTime,
      numberOfPersons: recipe.numberOfPersons,
      countries: (recipe.countries ?? []).map((item) => item.toUpperCase()),
      accessType: recipe.accessType,
      discountEnabled: !!recipe.discountEnabled,
    });

    this.ingredientsArray.clear();
    (recipe.ingredients ?? []).forEach((ingredient) => {
      this.ingredientsArray.push(this.createIngredientGroup(ingredient));
    });

    this.stepsArray.clear();
    const sortedSteps = [...(recipe.steps ?? [])].sort(
      (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
    );
    sortedSteps.forEach((step) => {
      this.stepsArray.push(this.createStepGroup(step));
    });
  }

  async submitForm(): Promise<void> {
    if (this.form.invalid || this.saving || this.uploadingCover) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildPayloadFromForm();
    if (!payload) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      if (this.editingRecipeId == null) {
        await firstValueFrom(this.recipesService.createRecipe(payload));
        this.successMessage = 'Recette créée avec succès.';
      } else {
        await firstValueFrom(this.recipesService.updateRecipe(this.editingRecipeId, payload));
        this.successMessage = 'Recette modifiée avec succès.';
      }

      this.resetForm();
      this.closeEditor();
      this.loadRecipes();
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Impossible d’enregistrer la recette.');
    } finally {
      this.saving = false;
    }
  }

  async deleteRecipe(recipe: AdminRecipe): Promise<void> {
    if (!this.canDelete(recipe)) {
      this.errorMessage = 'Suppression non autorisée pour cette recette.';
      return;
    }

    if (this.deletingRecipeId != null) {
      return;
    }

    const confirmed = window.confirm(`Supprimer la recette "${recipe.name}" ?`);
    if (!confirmed) {
      return;
    }

    this.deletingRecipeId = recipe.id;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await firstValueFrom(this.recipesService.deleteRecipe(recipe.id));
      this.successMessage = 'Recette supprimée avec succès.';
      this.allRecipes = this.allRecipes.filter((item) => item.id !== recipe.id);
      this.filteredRecipes = this.filteredRecipes.filter((item) => item.id !== recipe.id);
      this.totalItems = Math.max(0, this.totalItems - 1);
      this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
      if (this.currentPage >= this.totalPages) {
        this.currentPage = Math.max(0, this.totalPages - 1);
      }
      this.updateVisibleRecipes();

      if (this.editingRecipeId === recipe.id) {
        this.resetForm();
        this.closeEditor();
      }
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Suppression impossible.');
    } finally {
      this.deletingRecipeId = null;
    }
  }

  openIngredientDialog(index?: number): void {
    this.ingredientDialogIndex = typeof index === 'number' ? index : null;
    const control = typeof index === 'number' ? this.ingredientsArray.at(index) as FormGroup : null;

    this.ingredientDialogForm.reset({
      title: this.normalizeOptionalString(control?.get('title')?.value) ?? '',
      content: this.normalizeOptionalString(control?.get('content')?.value) ?? '',
      mandatory: control?.get('mandatory')?.value !== false,
      image: this.normalizeOptionalString(control?.get('image')?.value) ?? '',
      price: this.normalizeOptionalNumber(control?.get('price')?.value),
      priceFormatted: this.normalizeOptionalString(control?.get('priceFormatted')?.value) ?? '',
    });

    this.ingredientDialogOpen = true;
  }

  closeIngredientDialog(): void {
    this.ingredientDialogOpen = false;
    this.ingredientDialogIndex = null;
  }

  saveIngredientDialog(): void {
    const value = this.ingredientDialogForm.getRawValue();
    const group = this.createIngredientGroup({
      title: this.normalizeOptionalString(value.title),
      content: this.normalizeOptionalString(value.content),
      mandatory: value.mandatory !== false,
      image: this.normalizeOptionalString(value.image),
      price: this.normalizeOptionalNumber(value.price),
      priceFormatted: this.normalizeOptionalString(value.priceFormatted),
    });

    if (this.ingredientDialogIndex == null) {
      this.ingredientsArray.push(group);
    } else {
      this.ingredientsArray.setControl(this.ingredientDialogIndex, group);
    }

    this.closeIngredientDialog();
  }

  removeIngredient(index: number): void {
    this.ingredientsArray.removeAt(index);
  }

  addIngredient(): void {
    this.ingredientsArray.push(this.createIngredientGroup());
  }

  openStepDialog(index?: number): void {
    this.stepDialogIndex = typeof index === 'number' ? index : null;
    const control = typeof index === 'number' ? this.stepsArray.at(index) as FormGroup : null;

    this.stepDialogForm.reset({
      title: this.normalizeOptionalString(control?.get('title')?.value) ?? '',
      content: this.normalizeOptionalString(control?.get('content')?.value) ?? '',
      mandatory: control?.get('mandatory')?.value !== false,
      image: this.normalizeOptionalString(control?.get('image')?.value) ?? '',
    });

    this.stepDialogOpen = true;
  }

  closeStepDialog(): void {
    this.stepDialogOpen = false;
    this.stepDialogIndex = null;
  }

  saveStepDialog(): void {
    const value = this.stepDialogForm.getRawValue();
    const group = this.createStepGroup({
      title: this.normalizeOptionalString(value.title),
      content: this.normalizeOptionalString(value.content),
      mandatory: value.mandatory !== false,
      image: this.normalizeOptionalString(value.image),
    });

    if (this.stepDialogIndex == null) {
      this.stepsArray.push(group);
    } else {
      this.stepsArray.setControl(this.stepDialogIndex, group);
    }

    this.closeStepDialog();
  }

  removeStep(index: number): void {
    this.stepsArray.removeAt(index);
  }

  addStep(): void {
    this.stepsArray.push(this.createStepGroup());
  }

  async onCoverFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.item(0);
    if (!file || this.uploadingCover) {
      return;
    }

    this.uploadingCover = true;
    this.errorMessage = '';

    try {
      const fileName = await firstValueFrom(this.storageService.uploadFile(file));
      this.form.patchValue({ coverImage: fileName });
      this.successMessage = 'Image de couverture uploadée.';
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Upload de la couverture impossible.');
    } finally {
      this.uploadingCover = false;
      if (input) {
        input.value = '';
      }
    }
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.importFile = input?.files?.item(0) ?? null;
    this.importSummary = '';
    this.importFailures = [];
  }

  async importFromJsonFile(): Promise<void> {
    if (!this.importFile || this.importRunning) {
      return;
    }

    this.importRunning = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.importSummary = '';
    this.importFailures = [];

    try {
      const rawContent = await this.importFile.text();
      const parsed = JSON.parse(rawContent) as unknown;
      if (!Array.isArray(parsed)) {
        throw new Error('Le JSON doit contenir un tableau de recettes.');
      }

      let successCount = 0;
      const failures: string[] = [];

      for (let index = 0; index < parsed.length; index += 1) {
        try {
          const payload = this.buildPayloadFromImportedRecipe(parsed[index], index);
          await firstValueFrom(this.recipesService.createRecipe(payload));
          successCount += 1;
        } catch (error: unknown) {
          failures.push(`[${index + 1}] ${this.extractErrorMessage(error, 'Erreur d’import')}`);
        }
      }

      this.importSummary = `Import terminé: ${successCount}/${parsed.length} recette(s) importée(s).`;
      this.importFailures = failures;

      if (successCount > 0) {
        this.loadRecipes();
      }
    } catch (error: unknown) {
      this.errorMessage = this.extractErrorMessage(error, 'Import JSON impossible.');
    } finally {
      this.importRunning = false;
    }
  }

  categoryLabel(categoryId: number): string {
    return this.categories.find((item) => item.id === categoryId)?.name ?? `#${categoryId}`;
  }

  countryLabel(code: string): string {
    const normalizedCode = code.trim().toUpperCase();
    const country = this.countries.find((item) => item.code.toUpperCase() === normalizedCode);
    return country ? `${country.name} (${country.code})` : normalizedCode;
  }

  coverImageUrl(recipe: AdminRecipe): string | null {
    const rawCover = this.normalizeOptionalString(recipe.coverImage)
      ?? this.normalizeOptionalString(recipe.images?.[0]);

    if (!rawCover) {
      return null;
    }

    if (rawCover.startsWith('http://') || rawCover.startsWith('https://')) {
      return rawCover;
    }

    const normalizedCover = rawCover.replace(/^\/+/, '');
    return `${API_BASE_URL}/storage/files/${normalizedCover}`;
  }

  isDeleting(recipeId: number): boolean {
    return this.deletingRecipeId === recipeId;
  }

  canEdit(recipe: AdminRecipe): boolean {
    const role = this.currentRole();
    if (role === 'ADMIN_READ_ONLY' || role === 'USER' || role.length === 0) {
      return false;
    }

    if ((recipe.creatorType ?? '').toUpperCase() === 'USER') {
      return false;
    }

    const ownerId = recipe.ownerId;
    const memberId = this.auth.currentUser?.member?.id;
    if (ownerId != null && memberId != null) {
      return ownerId === memberId;
    }

    return false;
  }

  canDelete(_recipe: AdminRecipe): boolean {
    return this.currentRole() === 'ADMIN';
  }

  private currentRole(): string {
    return `${this.auth.currentUser?.role ?? ''}`.trim().toUpperCase();
  }

  private createIngredientGroup(ingredient?: RecipeIngredient): FormGroup {
    return this.fb.group({
      title: [ingredient?.title ?? ''],
      content: [ingredient?.content ?? ''],
      mandatory: [ingredient?.mandatory ?? true],
      image: [ingredient?.image ?? ''],
      price: [ingredient?.price ?? null],
      priceFormatted: [ingredient?.priceFormatted ?? ''],
    });
  }

  private createStepGroup(step?: RecipeStep): FormGroup {
    return this.fb.group({
      title: [step?.title ?? ''],
      content: [step?.content ?? ''],
      mandatory: [step?.mandatory ?? true],
      image: [step?.image ?? ''],
    });
  }

  private resetForm(): void {
    this.editingRecipeId = null;
    this.errorMessage = '';
    this.form.reset({
      name: '',
      description: '',
      cost: 'MEDIUM',
      coverImage: '',
      categoryId: null,
      difficulty: 'MEDIUM',
      cookingTime: 30,
      numberOfPersons: 2,
      countries: [] as string[],
      accessType: 'FREE',
      discountEnabled: false,
    });

    this.ingredientsArray.clear();
    this.stepsArray.clear();
  }

  private buildPayloadFromForm(): AdminRecipePayload | null {
    const name = this.normalizeRequiredString(this.form.get('name')?.value, 4);
    const description = this.normalizeRequiredString(this.form.get('description')?.value, 10);
    const coverImage = this.normalizeRequiredString(this.form.get('coverImage')?.value, 1);
    const categoryId = this.normalizeRequiredNumber(this.form.get('categoryId')?.value);
    const cookingTime = this.normalizeRequiredNumber(this.form.get('cookingTime')?.value);
    const numberOfPersons = this.normalizeRequiredNumber(this.form.get('numberOfPersons')?.value);
    const countries = this.parseCountries(this.form.get('countries')?.value);

    if (!name || !description || !coverImage || categoryId == null || cookingTime == null || numberOfPersons == null) {
      this.errorMessage = 'Le formulaire contient des champs invalides.';
      return null;
    }

    if (countries.length === 0) {
      this.errorMessage = 'Au moins un pays est requis.';
      return null;
    }

    const ingredients = this.ingredientsArray.controls.map((control) => this.mapIngredientGroup(control));
    if (ingredients.length === 0) {
      this.errorMessage = 'Ajoute au moins un ingrédient.';
      return null;
    }

    const steps = this.stepsArray.controls.map((control, index) => this.mapStepGroup(control, index + 1));
    if (steps.length === 0) {
      this.errorMessage = 'Ajoute au moins une étape.';
      return null;
    }

    return {
      name,
      description,
      cost: this.normalizeCost(this.form.get('cost')?.value),
      coverImage,
      images: [coverImage],
      categoryId,
      difficulty: this.normalizeDifficulty(this.form.get('difficulty')?.value),
      cookingTime,
      numberOfPersons,
      countries,
      ingredients,
      steps,
      accessType: this.normalizeAccessType(this.form.get('accessType')?.value),
      discountEnabled: !!this.form.get('discountEnabled')?.value,
    };
  }

  private buildPayloadFromImportedRecipe(raw: unknown, index: number): AdminRecipePayload {
    const record = this.asRecord(raw, `La recette #${index + 1} n'est pas un objet JSON valide.`);

    const name = this.normalizeRequiredString(record['name'], 4);
    const description = this.normalizeRequiredString(record['description'], 10);
    const coverImage = this.normalizeRequiredString(record['coverImage'], 1);
    const categoryId = this.normalizeRequiredNumber(record['categoryId']);
    const cookingTime = this.normalizeRequiredNumber(record['cookingTime']);
    const numberOfPersons = this.normalizeRequiredNumber(record['numberOfPersons']);

    if (!name || !description || !coverImage || categoryId == null || cookingTime == null || numberOfPersons == null) {
      throw new Error('Champs obligatoires manquants (name, description, coverImage, categoryId, cookingTime, numberOfPersons).');
    }

    const countries = this.parseCountries(record['countries']);
    if (countries.length === 0) {
      throw new Error('Le champ countries est vide.');
    }

    const ingredients = this.normalizeImportedIngredients(record['ingredients']);
    if (ingredients.length === 0) {
      throw new Error('Le champ ingredients est vide.');
    }

    const steps = this.normalizeImportedSteps(record['steps']);
    if (steps.length === 0) {
      throw new Error('Le champ steps est vide.');
    }

    const images = this.normalizeImportedImages(record['images'], coverImage);

    return {
      name,
      description,
      cost: this.normalizeCost(record['cost']),
      coverImage,
      images,
      categoryId,
      difficulty: this.normalizeDifficulty(record['difficulty']),
      cookingTime,
      numberOfPersons,
      countries,
      ingredients,
      steps,
      accessType: this.normalizeAccessType(record['accessType']),
      discountEnabled: !!record['discountEnabled'],
      priceAmount: this.normalizeOptionalNumber(record['priceAmount']),
      priceCurrency: this.normalizeOptionalString(record['priceCurrency']),
      discountAmount: this.normalizeOptionalNumber(record['discountAmount']),
      discountCurrency: this.normalizeOptionalString(record['discountCurrency']),
      discountStartAt: this.normalizeOptionalString(record['discountStartAt']),
      discountEndAt: this.normalizeOptionalString(record['discountEndAt']),
    };
  }

  private normalizeImportedIngredients(raw: unknown): RecipeIngredient[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.map((item) => {
      const record = this.asRecord(item, 'Un ingrédient est invalide.');
      return {
        title: this.normalizeOptionalString(record['title']),
        content: this.normalizeOptionalString(record['content']),
        mandatory: record['mandatory'] !== false,
        image: this.normalizeOptionalString(record['image']),
        price: this.normalizeOptionalNumber(record['price']),
        priceFormatted: this.normalizeOptionalString(record['priceFormatted']),
      };
    });
  }

  private normalizeImportedSteps(raw: unknown): RecipeStep[] {
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.map((item, index) => {
      const record = this.asRecord(item, 'Une étape est invalide.');
      return {
        order: this.normalizeRequiredNumber(record['order']) ?? index + 1,
        title: this.normalizeOptionalString(record['title']),
        content: this.normalizeOptionalString(record['content']),
        mandatory: record['mandatory'] !== false,
        image: this.normalizeOptionalString(record['image']),
      };
    });
  }

  private normalizeImportedImages(raw: unknown, coverImage: string): string[] {
    if (!Array.isArray(raw)) {
      return [coverImage];
    }

    const normalized = raw
      .map((item) => this.normalizeOptionalString(item))
      .filter((item): item is string => !!item);

    if (normalized.length === 0) {
      return [coverImage];
    }

    if (!normalized.includes(coverImage)) {
      normalized.unshift(coverImage);
    }

    return normalized;
  }

  private mapIngredientGroup(control: AbstractControl): RecipeIngredient {
    const group = control as FormGroup;
    return {
      title: this.normalizeOptionalString(group.get('title')?.value),
      content: this.normalizeOptionalString(group.get('content')?.value),
      mandatory: group.get('mandatory')?.value !== false,
      image: this.normalizeOptionalString(group.get('image')?.value),
      price: this.normalizeOptionalNumber(group.get('price')?.value),
      priceFormatted: this.normalizeOptionalString(group.get('priceFormatted')?.value),
    };
  }

  private mapStepGroup(control: AbstractControl, order: number): RecipeStep {
    const group = control as FormGroup;
    return {
      order,
      title: this.normalizeOptionalString(group.get('title')?.value),
      content: this.normalizeOptionalString(group.get('content')?.value),
      mandatory: group.get('mandatory')?.value !== false,
      image: this.normalizeOptionalString(group.get('image')?.value),
    };
  }

  private parseCountries(raw: unknown): string[] {
    if (Array.isArray(raw)) {
      return raw
        .map((item) => this.normalizeOptionalString(item))
        .filter((item): item is string => !!item)
        .map((item) => item.toUpperCase());
    }

    const value = this.normalizeOptionalString(raw);
    if (!value) {
      return [];
    }

    return value
      .split(/[,\n;]/)
      .map((item) => item.trim().toUpperCase())
      .filter((item) => item.length > 0);
  }

  private normalizeRequiredString(value: unknown, minLength: number): string | null {
    const normalized = this.normalizeOptionalString(value);
    if (!normalized || normalized.length < minLength) {
      return null;
    }
    return normalized;
  }

  private normalizeOptionalString(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  private normalizeRequiredNumber(value: unknown): number | null {
    const normalized = this.normalizeOptionalNumber(value);
    if (normalized == null || normalized <= 0) {
      return null;
    }
    return normalized;
  }

  private normalizeOptionalNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return null;
  }

  private normalizeCost(value: unknown): RecipeCost {
    const allowed: RecipeCost[] = ['CHEAP', 'MEDIUM', 'EXPENSIVE'];
    return typeof value === 'string' && allowed.includes(value as RecipeCost) ? (value as RecipeCost) : 'MEDIUM';
  }

  private normalizeDifficulty(value: unknown): RecipeDifficulty {
    const allowed: RecipeDifficulty[] = ['EASY', 'MEDIUM', 'HARD'];
    return typeof value === 'string' && allowed.includes(value as RecipeDifficulty)
      ? (value as RecipeDifficulty)
      : 'MEDIUM';
  }

  private normalizeAccessType(value: unknown): RecipeAccessType {
    const allowed: RecipeAccessType[] = ['FREE', 'SUBSCRIBERS_ONLY', 'PAID'];
    return typeof value === 'string' && allowed.includes(value as RecipeAccessType)
      ? (value as RecipeAccessType)
      : 'FREE';
  }

  private asRecord(value: unknown, message: string): Record<string, unknown> {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
    throw new Error(message);
  }

  private extractErrorMessage(error: unknown, fallback: string): string {
    if (typeof error === 'string' && error.trim().length > 0) {
      return error;
    }

    if (error && typeof error === 'object') {
      const errorRecord = error as { error?: { message?: unknown }; message?: unknown };
      if (typeof errorRecord.error?.message === 'string' && errorRecord.error.message.trim().length > 0) {
        return errorRecord.error.message;
      }
      if (typeof errorRecord.message === 'string' && errorRecord.message.trim().length > 0) {
        return errorRecord.message;
      }
    }

    return fallback;
  }

  private updateVisibleRecipes(): void {
    const start = this.currentPage * this.pageSize;
    this.recipes = this.filteredRecipes.slice(start, start + this.pageSize);
  }

  private async fetchAllRecipes(): Promise<AdminRecipe[]> {
    const recipesByStatus = await Promise.all(
      this.statusesToLoad.map((status) => this.fetchRecipesByStatus(status))
    );

    const deduplicated = new Map<number, AdminRecipe>();
    recipesByStatus.flat().forEach((recipe) => {
      deduplicated.set(recipe.id, recipe);
    });

    return Array.from(deduplicated.values()).sort((a, b) => {
      const aTimestamp = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bTimestamp = b.createdAt ? Date.parse(b.createdAt) : 0;
      if (aTimestamp !== bTimestamp) {
        return bTimestamp - aTimestamp;
      }
      return b.id - a.id;
    });
  }

  private async fetchRecipesByStatus(status: string): Promise<AdminRecipe[]> {
    const items: AdminRecipe[] = [];
    const pageSize = 100;
    let page = 0;
    let totalPages = 1;

    do {
      const response = await firstValueFrom(this.recipesService.getRecipesByStatus(status, page, pageSize));
      items.push(...(response.items ?? []));
      totalPages = Math.max(1, response.totalPages ?? 1);
      page += 1;
    } while (page < totalPages);

    return items;
  }
}
